import React, {PureComponent} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {SkinTonesEnum} from '../../../../models/api/preferencesModel';
import {EmojiDropdownCategories, EmojiDropdownCategoriesProps} from './emojiDropdownCategories';
import {EmojiDropdownSkinTones, EmojiDropdownSkinTonesProps} from './emojiDropdownSkinTones';

/*
 * Props.
 */

type EmojiDropdownToolbarProps = EmojiDropdownCategoriesProps & EmojiDropdownSkinTonesProps;

/*
 * Style.
 */

const StyledToolbarDiv = styled.div`
  height: 38px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  display: flex;
`;

const StyledLeftDiv = styled(ChromelessButton)`
  align-self: center;
  margin: 0 8px;
  padding: 6px;

  border-radius: 4px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledSeparatorDiv = styled.div`
  align-self: center;
  width: 1px;
  height: 16px;
  background-color: ${(p) => p.theme.greys.shade30};
`;

interface ToneStyleProps {
  $skinTone: SkinTonesEnum;
}
const StyledToneDiv = styled.div<ToneStyleProps>`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${(p) => skinToneColors[p.$skinTone]};
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.alphas.black20};
`;

const StyledChevronShape = styled(Icon)`
  transform: rotateZ(90deg);
`;

type SkinToneColors = {[T in SkinTonesEnum]: string};
const skinToneColors: SkinToneColors = {
  [SkinTonesEnum.NONE]: '#fcc22f',
  [SkinTonesEnum.LIGHT]: '#f8e0be',
  [SkinTonesEnum.MEDIUM_LIGHT]: '#e5bf9b',
  [SkinTonesEnum.MEDIUM]: '#c39169',
  [SkinTonesEnum.MEDIUM_DARK]: '#9b653e',
  [SkinTonesEnum.DARK]: '#5a4639',
};

/*
 * Component.
 */

interface EmojiDropdownToolbarState {
  isSkinTonePickerOpen: boolean;
}

export class EmojiDropdownToolbar extends PureComponent<
  EmojiDropdownToolbarProps,
  EmojiDropdownToolbarState
> {
  constructor(props: EmojiDropdownToolbarProps) {
    super(props);
    this.state = {isSkinTonePickerOpen: false};
  }

  /*
   * Event handlers.
   */

  private readonly toggleIsSkinTonPickerOpen = () => {
    this.setState(({isSkinTonePickerOpen}) => ({isSkinTonePickerOpen: !isSkinTonePickerOpen}));
  };

  private readonly onSkinToneSelect: HandlerOf<SkinTonesEnum> = (skinTone) => {
    this.toggleIsSkinTonPickerOpen();
    this.props.onSkinToneSelect(skinTone);
  };

  /*
   * Render.
   */

  render() {
    const {isSkinTonePickerOpen} = this.state;
    return (
      <StyledToolbarDiv>
        {isSkinTonePickerOpen ? this.renderSkinTones() : this.renderCategories()}
      </StyledToolbarDiv>
    );
  }

  private renderCategories() {
    return (
      <>
        <StyledLeftDiv onClick={this.toggleIsSkinTonPickerOpen}>
          <StyledToneDiv $skinTone={this.props.skinTone} />
        </StyledLeftDiv>
        <StyledSeparatorDiv />
        <EmojiDropdownCategories {...this.props} />
      </>
    );
  }

  private renderSkinTones() {
    return (
      <>
        <StyledLeftDiv onClick={this.toggleIsSkinTonPickerOpen}>
          <StyledChevronShape name="chevronDownSmall" />
        </StyledLeftDiv>
        <StyledSeparatorDiv />
        <EmojiDropdownSkinTones {...this.props} onSkinToneSelect={this.onSkinToneSelect} />
      </>
    );
  }
}
